import "./single.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
// import Chart from "../../components/chart/Chart";
// import List from "../../components/table/Table";
import { useParams } from "react-router-dom";
import React, { Fragment, useEffect, useState, useRef } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  collection,
} from "firebase/firestore";
import { db } from "../../firebase";

import {
  ingredientInputs,
  doughInputs,
  productInputs,
  userInputs,
  ingredientVolumeInputs, // Import volume inputs
  lineInputs,
  ovenInputs,
  productionListInputs,
} from "../../formSource";

import InventoryForm from "../../components/inventoryForm/inventoryForm";
import ProductionListsForm from "../new/ProductionListsForm.jsx";

import { MASS_UNITS, VOLUME_UNITS } from "../../constants";

const Single = () => {
  const { type, objectId } = useParams();
  // console.log("Inside Single Page: type & ID:  ", type, objectId);
  const [ingredients, setIngredients] = useState([]);
  const [doughs, setDoughs] = useState([]);
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  //USED TO TRACK SELECTED UNITS FOR CUSTOM INGREDIENT UNITS
  const [selectedIngredientUnits, setSelectedIngredientUnits] = useState([]);

  const [conversionRates, setConversionRates] = useState([]);

  const [minMixUnit, setMinMixUnit] = useState(MASS_UNITS[0]);
  const [maxMixUnit, setMaxMixUnit] = useState(MASS_UNITS[0]);

  const organizationId = JSON.parse(localStorage.getItem("organizations"))
    .selected.id;

  const formConfig = {
    ingredients: { inputs: ingredientInputs, title: "Ingredient Information" },
    doughs: { inputs: doughInputs, title: "Dough Information" },
    products: { inputs: productInputs, title: "Product Information" },
    users: { inputs: userInputs, title: "User Information" },
    lines: { inputs: lineInputs, title: "Production Line Information" },
    ovens: { inputs: ovenInputs, title: "Oven Information" },
    productionlists: {
      inputs: productionListInputs,
      title: "Production List Information",
    },
  };

  const [documentData, setDocumentData] = useState(null); // State to hold document data
  const [editMode, setEditMode] = useState(false); // State to toggle edit mode
  const [editedData, setEditedData] = useState(null); // State to hold edited data

  // Authoritative flattened representation for editing productionlists
  // [{ lineId: "lines/<id>", productId: "products/<id>" }, ...]
  const [flattenedLinesAndProducts, setFlattenedLinesAndProducts] = useState(
    []
  );
  const flattenedRef = useRef(flattenedLinesAndProducts);
  useEffect(() => {
    flattenedRef.current = flattenedLinesAndProducts;
  }, [flattenedLinesAndProducts]);

  const fetchItems = (collectionName, stateUpdater) => {
    return onSnapshot(
      collection(db, "organizations", organizationId, collectionName),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: `${collectionName}/${doc.id}`,
          collection: collectionName,
          ...doc.data(),
        }));
        stateUpdater(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  useEffect(() => {
    const unsubscribeIngredients = fetchItems("ingredients", setIngredients);
    const unsubscribeDoughs = fetchItems("doughs", setDoughs);
    const unsubscribeProducts = fetchItems("products", setProducts);
    const unsubscribeLines = fetchItems("lines", setLines);
    const fetchData = async () => {
      const docRef = doc(db, "organizations", organizationId, type, objectId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const raw = docSnap.data();
        setDocumentData(raw); // Update state with document data
        // Initialize editedData only when entering edit mode (handleEdit)
      } else {
        console.log("No such document!");
      }
    };

    fetchData(); // Call the async function to fetch document data
    return () => {
      unsubscribeIngredients();
      unsubscribeDoughs();
      unsubscribeProducts();
      unsubscribeLines();
    };
  }, [type, objectId, organizationId]); // Run effect when type or objectId changes

  // const allItems = [...ingredients, ...doughs, ...products];
  const sortedIngredients = [...ingredients].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const sortedDoughs = [...doughs].sort((a, b) => a.name.localeCompare(b.name));
  const sortedProducts = [...products].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const allItems = [...sortedIngredients, ...sortedDoughs, ...sortedProducts];

  const sortedLines = [...lines].sort((a, b) => a.name.localeCompare(b.name));
  // console.log("allLines ", allLines)

  const toNumberOrZero = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeOvenProducts = (products) => {
    if (!products) return [];
    if (Array.isArray(products)) return products;
    if (typeof products !== "object") return [];
    return Object.entries(products).map(([productRef, config]) => ({
      [productRef]: {
        piecesPerHour: toNumberOrZero(config?.piecesPerHour),
        capacity: toNumberOrZero(config?.capacity),
        bakeTimeMinutes: toNumberOrZero(config?.bakeTimeMinutes),
        preheatMinutes: toNumberOrZero(config?.preheatMinutes),
        cooldownMinutes: toNumberOrZero(config?.cooldownMinutes),
      },
    }));
  };

  /***********************
   * Helpers for productionlists normalization and utilities
   ***********************/
  const isFlattenedFormat = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return false;
    const first = arr[0];
    return (
      first &&
      (Object.prototype.hasOwnProperty.call(first, "lineId") ||
        Object.prototype.hasOwnProperty.call(first, "productId"))
    );
  };

  // Normalize either grouped or flattened into canonical flattened array
  const normalizeToFlattened = (raw) => {
    if (!raw) return [];
    if (isFlattenedFormat(raw)) {
      return raw
        .map((it) => {
          if (!it) return null;
          if (it.lineId && it.productId) return { lineId: it.lineId, productId: it.productId };
          const lineId = it.lineId || it.line || it.lineRef;
          const productId = it.productId || it.product || it.productRef;
          if (lineId && productId) return { lineId, productId };
          return null;
        })
        .filter(Boolean);
    }

    const flattened = [];
    if (Array.isArray(raw)) {
      raw.forEach((entry) => {
        if (!entry || typeof entry !== "object") return;
        const keys = Object.keys(entry);
        keys.forEach((lineKey) => {
          const list = entry[lineKey] || [];
          if (Array.isArray(list)) {
            list.forEach((prod) => {
              if (prod) {
                const normalizedLineRef = lineKey.startsWith("lines/") ? lineKey : `lines/${lineKey}`;
                flattened.push({ lineId: normalizedLineRef, productId: prod });
              }
            });
          }
        });
      });
    }
    return flattened;
  };

  // Group flattened for display: preserves order of flattened list for lines
  const groupFlattenedForDisplay = (flattened) => {
    const map = {};
    const order = [];
    (flattened || []).forEach((pair) => {
      if (!pair || !pair.lineId || !pair.productId) return;
      const lid = pair.lineId;
      if (!map[lid]) {
        map[lid] = [];
        order.push(lid);
      }
      map[lid].push(pair.productId);
    });
    return order.map((lid) => ({ lineRef: lid, products: map[lid] }));
  };

  // Robust finder helpers that accept either "collection/id" or plain id
  const findLineByRef = (ref) => {
    if (!ref) return null;
    const refStr = String(ref);
    const plain = refStr.split("/").pop();
    // match suffix
    let line = sortedLines.find((l) => {
      const lid = String(l.id);
      return lid === refStr || lid === `lines/${plain}` || lid === plain || lid.split("/").pop() === plain;
    });
    return line || null;
  };

  const findItemByRef = (ref) => {
    if (!ref) return null;
    const refStr = String(ref);
    const plain = refStr.split("/").pop();
    let item = allItems.find((i) => {
      const iid = String(i.id);
      return iid === refStr || iid === `products/${plain}` || iid === `ingredients/${plain}` || iid === plain || iid.split("/").pop() === plain;
    });
    return item || null;
  };

  /***********************
   * Productionlists editor operations (flat-first)
   ***********************/
  // These functions are required by the ProductionListsForm props.
  // They must exist in scope before JSX uses them.
  const addBlankPair = () => {
    setFlattenedLinesAndProducts((prev) => [...prev, { lineId: "", productId: "" }]);
  };

  const updatePair = (index, updates) => {
    setFlattenedLinesAndProducts((prev) => {
      const copy = prev.slice();
      copy[index] = { ...copy[index], ...updates };
      // if line changed and product isn't compatible with new line, clear productId
      if (updates.lineId) {
        const lineObj = findLineByRef(updates.lineId);
        if (lineObj) {
          const productRefs = (lineObj.products || []).map((p) => Object.keys(p)[0]);
          if (!productRefs.includes(copy[index].productId)) {
            copy[index].productId = "";
          }
        }
      }
      return copy;
    });
  };

  const removePair = (index) => {
    setFlattenedLinesAndProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const movePair = (fromIndex, toIndex) => {
    setFlattenedLinesAndProducts((prev) => {
      const copy = prev.slice();
      const [item] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, item);
      return copy;
    });
  };

  const getIdFromRef = (ref) => {
    if (!ref) return "";
    const parts = String(ref).split("/");
    return parts.length > 1 ? parts[1] : ref;
  };

  const productsForLine = (lineIdRef) => {
    const line = findLineByRef(lineIdRef);
    if (!line || !Array.isArray(line.products)) return [];
    return line.products.map((p) => Object.keys(p)[0]);
  };

  const addAllProductsForLine = (lineIdRef, insertAt = null) => {
    const lineObj = findLineByRef(lineIdRef);
    if (!lineObj || !Array.isArray(lineObj.products)) return;

    const toAppend = lineObj.products.map((p) => Object.keys(p)[0]); // productRef strings
    setFlattenedLinesAndProducts((prev) => {
      const existingSet = new Set(prev.map((p) => `${p.lineId}|${p.productId}`));
      const newPairs = [];
      toAppend.forEach((prodRef) => {
        const key = `${lineObj.id}|${prodRef}`;
        if (!existingSet.has(key)) {
          newPairs.push({ lineId: lineObj.id, productId: prodRef });
          existingSet.add(key);
        }
      });
      if (newPairs.length === 0) return prev;
      if (insertAt === null || insertAt < 0 || insertAt >= prev.length) {
        return [...prev, ...newPairs];
      } else {
        const copy = prev.slice();
        copy.splice(insertAt + 1, 0, ...newPairs);
        return copy;
      }
    });
  };

  // Build set of used productRefs, optionally excluding a specific index (so editing that row can keep its product)
  function usedProductSetExcludingIndex(excludeIndex = null) {
    const s = new Set();
    flattenedLinesAndProducts.forEach((p, i) => {
      if (!p) return;
      if (i === excludeIndex) return;
      if (p.productId) s.add(p.productId);
    });
    return s;
  }

  function availableProductsForLine(lineIdRef, rowIndex = null) {
    const prods = productsForLine(lineIdRef);
    if (!prods || prods.length === 0) return [];
    const used = usedProductSetExcludingIndex(rowIndex);
    return prods.filter((prodRef) => !used.has(prodRef));
  }

  function isLineSelectable(lineIdRef, rowIndex = null) {
    const avail = availableProductsForLine(lineIdRef, rowIndex);
    return avail.length > 0;
  }

  /***********************
   * Handle edit initialization (preserve original behavior for ingredients/doughs/products)
   ***********************/
  const handleEdit = () => {
    setEditMode(true);
    if (documentData) {
      const draft = { ...documentData };
      if (type === "ovens") {
        draft.products = normalizeOvenProducts(documentData.products);
      }
      setEditedData(draft);
    } else {
      setEditedData(null);
    }
    // If productionlists, initialize flattened state
    if (type === "productionlists" && documentData) {
      const normalized = normalizeToFlattened(documentData.linesAndProducts);
      setFlattenedLinesAndProducts(normalized);
    }

    // Update selectedUnits for each ingredient in editedData.ingredients (original behavior)
    if (documentData && documentData.ingredients) {
      const updatedSelectedUnits = documentData.ingredients.map(
        () => ({
          isVolume: false,
          unit: MASS_UNITS[0],
        })
      );
      setSelectedUnits(updatedSelectedUnits);

      const updatedConversionRates = documentData.ingredients.map(
        (ingredient) => {
          const key = Object.keys(ingredient)[0];
          const selectedIngredient = allItems.find((item) => item.id === key);
          return selectedIngredient?.massconversion || null;
        }
      );
      setConversionRates(updatedConversionRates);
    }
  };

  //BASIC FORM INFO CHANGE HANDLING (original)
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setEditedData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // --- ORIGINAL handlers for ingredients/doughs/products/lines retained exactly ---
  const handleAddIngredient = (typeParam) => {
    const newKey =
      typeParam === "doughs" ? `doughs-${Date.now()}` : `ingredients-${Date.now()}`;
    const newEditedIngredients = [...(editedData?.ingredients || []), { [newKey]: 0 }];

    const newSelectedUnits = [
      ...selectedUnits,
      {
        isVolume: false,
        unit: MASS_UNITS[0],
      },
    ];

    setEditedData((prevData) => ({
      ...prevData,
      ingredients: newEditedIngredients,
    }));

    setSelectedUnits(newSelectedUnits);
  };

  const handleAddIngredientUnit = () => {
    const newIngredientUnits = [...(editedData?.ingredientUnits || []), { "": 0 }];
    setEditedData((prev) => ({
      ...prev,
      ingredientUnits: newIngredientUnits,
    }));
  };

  const handleAddProduct = () => {
    const newEditedProducts = [...(editedData?.products || []), { "": {} }];

    setEditedData((prev) => ({
      ...prev,
      products: newEditedProducts,
    }));
  };

  const handleAddProductUnit = () => {
    const newEditedProductUnitss = [
      ...(editedData?.productUnits || []),
      { name: "", pieceCount: 0 },
    ];
    setEditedData((prev) => ({
      ...prev,
      productUnits: newEditedProductUnitss,
    }));
  };

  const handleAddLine = () => {
    const newEditedLines = [...(editedData?.linesAndProducts || []), { "": [] }];

    setEditedData((prev) => ({
      ...prev,
      linesAndProducts: newEditedLines,
    }));
  };

  const handleAddProductToLine = (lineIndex, lineRef) => {
    const newEditedLines = [...(editedData?.linesAndProducts || [])];
    let lineData = newEditedLines[lineIndex];
    lineData[lineRef].push("");
    newEditedLines[lineIndex] = lineData;
    setEditedData((prev) => ({
      ...prev,
      linesAndProducts: newEditedLines,
    }));
  };

  const handleIngredientSelectChange = (e, index) => {
    const { value } = e.target;
    const selectedIngredient = allItems.find(
      (ingredient) => ingredient.id === value
    );
    const newIngredients = [...(editedData?.ingredients || [])];
    newIngredients[index] = {
      [selectedIngredient.id]: "",
    };
    setEditedData((prevData) => ({ ...prevData, ingredients: newIngredients }));

    const isVolume = selectedIngredient.volume;
    if (isVolume) {
      const newConversionRates = [...conversionRates];
      newConversionRates[index] = selectedIngredient.massconversion;
      setConversionRates(newConversionRates);
    }
    const defaultUnit = MASS_UNITS[0];
    const newSelectedUnits = [...selectedUnits];
    newSelectedUnits[index] = { unit: defaultUnit, isVolume };
    setSelectedUnits(newSelectedUnits);
  };

  const handleProductSelectChange = (e, index) => {
    const { value } = e.target;
    const selectedProduct = allItems.find(
      (ingredient) => ingredient.id === value
    );
    const newProducts = [...(editedData?.products || [])];
    newProducts[index] = {
      [selectedProduct.id]: {},
    };
    setEditedData((prevData) => ({ ...prevData, products: newProducts }));
  };

  const handleProductChange = (index, key, e) => {
    const { name, value } = e.target;
    const newProducts = [...(editedData?.products || [])];
    newProducts[index][key] = {
      ...newProducts[index][key],
      [name]: value,
    };
    setEditedData((prev) => ({ ...prev, products: newProducts }));
  };

  const handleLineSelectChange = (e, index) => {
    const { value } = e.target;
    const selectedLine = lines.find((line) => line.id === value || line.id.split("/").pop() === value);
    const newLines = [...(editedData?.linesAndProducts || [])];
    newLines[index] = {
      [selectedLine.id]: [],
    };
    setEditedData((prevData) => ({ ...prevData, linesAndProducts: newLines }));
  };

  const handleProductLineSelectChange = (
    e,
    lineIndex,
    lineRef,
    productIndex
  ) => {
    const selectedProductId = e.target.value;
    const newEditedLines = [...(editedData?.linesAndProducts || [])];
    let lineData = newEditedLines[lineIndex];

    if (
      lineData &&
      lineData[lineRef] &&
      lineData[lineRef].length > productIndex
    ) {
      lineData[lineRef][productIndex] = selectedProductId;
    }
    newEditedLines[lineIndex] = lineData;

    setEditedData((prevData) => ({
      ...prevData,
      linesAndProducts: newEditedLines,
    }));
  };

  const handleMixUnitChange = (e) => {
    const { value, id } = e.target;
    const unit = MASS_UNITS.find((unit) => unit.abbreviation === value);
    if (id === "minMix") {
      setMinMixUnit(unit);
    } else {
      setMaxMixUnit(unit);
    }
  };

  const handleMixInput = (e) => {
    let mixValue = 0;
    const { id, value } = e.target;
    if (id === "minMix") {
      mixValue = value * minMixUnit.amount;
    } else {
      mixValue = value * maxMixUnit.amount;
    }

    setEditedData((prevData) => ({ ...prevData, [id]: mixValue }));
  };

  const handleIngredientChange = (index, key, value) => {
    const newIngredients = [...(editedData?.ingredients || [])];
    if (selectedUnits[index].isVolume) {
      newIngredients[index][key] =
        value * selectedUnits[index].unit.amount * conversionRates[index];
    } else {
      newIngredients[index][key] = value * selectedUnits[index].unit.amount;
    }
    setEditedData((prev) => ({ ...prev, ingredients: newIngredients }));
  };

  const handleUnitChange = (e, index) => {
    const { value } = e.target;

    let isVolume = false;
    let unit = VOLUME_UNITS.find((unit) => unit.abbreviation === value);

    if (!unit) {
      unit = MASS_UNITS.find((unit) => unit.abbreviation === value);
      isVolume = false;
    } else {
      isVolume = true;
    }

    if (unit) {
      const newSelectedUnits = [...selectedUnits];
      newSelectedUnits[index] = { unit, isVolume };
      setSelectedUnits(newSelectedUnits);
    }
  };

  const handleIngredientWeightUnitChange = (e, index) => {
    const { value } = e.target;

    let isVolume = false;
    let unit = VOLUME_UNITS.find((unit) => unit.abbreviation === value);

    if (!unit) {
      unit = MASS_UNITS.find((unit) => unit.abbreviation === value);
      isVolume = false;
    } else {
      isVolume = true;
    }

    if (unit) {
      const newSelectedUnits = [...selectedIngredientUnits];
      newSelectedUnits[index] = unit;
      setSelectedIngredientUnits(newSelectedUnits);
    }
  };

  const handleProductUnitChange = (index, value, caseOfEvent) => {
    let productUnits = editedData.productUnits;
    if (caseOfEvent === "name") {
      productUnits[index].name = value;
    }
    if (caseOfEvent === "count") {
      productUnits[index].pieceCount = value;
    }
    setEditedData((prevData) => ({
      ...prevData,
      productUnits: productUnits,
    }));
  };

  const handleIngredientUnitChange = (index, value, field) => {
    setEditedData((prevData) => {
      const updatedIngredientUnits = [...prevData.ingredientUnits];
      const targetMap = { ...updatedIngredientUnits[index] };

      if (field === "name") {
        const oldKey = Object.keys(targetMap)[0];
        const number = targetMap[oldKey];
        delete targetMap[oldKey];
        targetMap[value] = number;
      } else if (field === "count") {
        const key = Object.keys(targetMap)[0];
        if (selectedIngredientUnits[index]) {
          if (
            editedData.volume &&
            VOLUME_UNITS.find(
              (unit) =>
                unit.abbreviation ===
                selectedIngredientUnits[index].abbreviation
            )
          ) {
            targetMap[key] =
              parseFloat(value) *
              selectedIngredientUnits[index].amount *
              editedData.massconversion;
          } else {
            targetMap[key] =
              parseFloat(value) * selectedIngredientUnits[index].amount;
          }
        } else {
          targetMap[key] = parseFloat(value) * MASS_UNITS[0].amount;
        }
      }

      updatedIngredientUnits[index] = targetMap;

      return {
        ...prevData,
        ingredientUnits: updatedIngredientUnits,
      };
    });
  };

  const handleRemoveItem = (index, caseOfEvent, subIndex) => {
    if (caseOfEvent === "ingredients") {
      const newIngredients = [...(editedData?.ingredients || [])];
      newIngredients.splice(index, 1);
      setEditedData((prevData) => ({
        ...prevData,
        ingredients: newIngredients,
      }));

      const newSelectedUnits = [...selectedUnits];
      newSelectedUnits.splice(index, 1);
      setSelectedUnits(newSelectedUnits);

      const newConversionRates = [...conversionRates];
      newConversionRates.splice(index, 1);
      setConversionRates(newConversionRates);
    }
    if (caseOfEvent === "products") {
      const newProducts = [...(editedData?.products || [])];
      newProducts.splice(index, 1);
      setEditedData((prevData) => ({ ...prevData, products: newProducts }));
    }
    if (caseOfEvent === "lines") {
      const newLines = [...(editedData?.linesAndProducts || [])];
      newLines.splice(index, 1);
      setEditedData((prevData) => ({
        ...prevData,
        linesAndProducts: newLines,
      }));
    }
    if (caseOfEvent === "lineProducts") {
      const newLines = (editedData?.linesAndProducts || []).map((line) => ({ ...line }));
      const lineId = Object.keys(newLines[index])[0];
      const updatedProducts = newLines[index][lineId].filter(
        (_, idx) => idx !== subIndex
      );
      newLines[index][lineId] = updatedProducts;

      setEditedData((prevData) => ({
        ...prevData,
        linesAndProducts: newLines,
      }));
    }
    if (caseOfEvent === "productUnits") {
      const newProductUnits = [...(editedData?.productUnits || [])];
      newProductUnits.splice(index, 1);
      setEditedData((prevData) => ({
        ...prevData,
        productUnits: newProductUnits,
      }));
    }
    if (caseOfEvent === "ingredientUnits") {
      const newIngredientUnits = [...(editedData?.ingredientUnits || [])];
      newIngredientUnits.splice(index, 1);
      setEditedData((prevData) => ({
        ...prevData,
        ingredientUnits: newIngredientUnits,
      }));
    }
  };

  const moveLine = (index, direction) => {
    const newLines = [...(editedData?.linesAndProducts || [])];
    const temp = newLines[index];
    newLines[index] = newLines[index + direction];
    newLines[index + direction] = temp;
    setEditedData((prev) => ({ ...prev, linesAndProducts: newLines }));
  };

  const moveProduct = (lineIndex, productIndex, direction) => {
    const newLines = [...(editedData?.linesAndProducts || [])];
    const lineRef = Object.keys(newLines[lineIndex])[0];
    const productsArr = newLines[lineIndex][lineRef];
    const temp = productsArr[productIndex];
    productsArr[productIndex] = productsArr[productIndex + direction];
    productsArr[productIndex + direction] = temp;
    setEditedData((prev) => ({ ...prev, linesAndProducts: newLines }));
  };

  const handleAddAllProductsToLine = (lineIndex, lineRef, newProducts) => {
    if (!lineRef || !newProducts || newProducts.length === 0) return;

    setEditedData((prev) => {
      const updated = [...(prev.linesAndProducts || [])];
      const lineEntry = { ...updated[lineIndex] };
      const productsArray = [...(lineEntry[lineRef] || [])];

      newProducts.forEach((p) => {
        if (!productsArray.includes(p)) {
          productsArray.push(p);
        }
      });

      lineEntry[lineRef] = productsArray;
      updated[lineIndex] = lineEntry;

      return { ...prev, linesAndProducts: updated };
    });
  };

  //Functions to find corresponding data.
  const getItemNameById = (id) => {
    const item = allItems.find((item) => item.id === id);
    return item ? item.name : "Unknown";
  };
  const getItemById = (id) => {
    const item = allItems.find((item) => item.id === id);
    return item ? item : "Unknown";
  };

  const getLineById = (id) => {
    const item = sortedLines.find((item) => item.id === id);
    return item ? item : "Unknown";
  };
  const getItemHydrationById = (id) => {
    const item = allItems.find((item) => item.id === id);
    return item.hydration ? item.hydration : 0;
  };
  const getTotalValue = (ingredients) => {
    return ingredients.reduce((sum, ingredientMap) => {
      return sum + Object.values(ingredientMap)[0];
    }, 0);
  };
  const getTotalHydration = (ingredients) => {
    let totalHydration = 0;
    let totalDry = 0;

    ingredients.forEach((ingredientMap) => {
      const ingredientId = Object.keys(ingredientMap)[0];
      const ingredientAmount = Object.values(ingredientMap)[0];
      const hydrationPercentage = getItemHydrationById(ingredientId);

      totalHydration += (hydrationPercentage / 100) * ingredientAmount;
      totalDry += (1 - hydrationPercentage / 100) * ingredientAmount;
    });
    return (totalHydration / totalDry) * 100;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const docRef = doc(db, "organizations", organizationId, type, objectId);

    // If this is a production list, ensure we persist flattenedLinesAndProducts
    if (type === "productionlists") {
      const payload = {
        ...editedData,
        linesAndProducts: flattenedLinesAndProducts,
      };
      await updateDoc(docRef, payload);
      setDocumentData(payload);
    } else if (type === "ovens") {
      const payload = {
        ...editedData,
        capacity: toNumberOrZero(editedData.capacity),
        bakeTimeMinutes: toNumberOrZero(editedData.bakeTimeMinutes),
        preheatMinutes: toNumberOrZero(editedData.preheatMinutes),
        cooldownMinutes: toNumberOrZero(editedData.cooldownMinutes),
        throughputPerHour: toNumberOrZero(editedData.throughputPerHour),
        products: (editedData.products || [])
          .map((productMap) => {
            const [productRef, config] = Object.entries(productMap || {})[0] || [];
            if (!productRef) return null;
            return {
              [productRef]: {
                piecesPerHour: toNumberOrZero(config?.piecesPerHour),
                capacity: toNumberOrZero(config?.capacity),
                bakeTimeMinutes: toNumberOrZero(config?.bakeTimeMinutes),
                preheatMinutes: toNumberOrZero(config?.preheatMinutes),
                cooldownMinutes: toNumberOrZero(config?.cooldownMinutes),
              },
            };
          })
          .filter(Boolean),
      };
      await updateDoc(docRef, payload);
      setDocumentData(payload);
    } else {
      await updateDoc(docRef, editedData); // Update document in Firestore
      setDocumentData(editedData);
    }

    setEditMode(false); // Disable edit mode after saving
  };

  return (
    <div className="single">
      {/* {console.log("allItems ", allItems)} */}
      {/* {console.log("editedData ", editedData)} */}
      {/* {console.log("selectedUnits ", selectedUnits)}
      {console.log("conversionRates ", conversionRates)} */}
      <Sidebar />
      <div className="singleContainer">
        <Navbar />
        <div className="top">
          <div className="left">
            <div className="editButton" onClick={handleEdit}>
              Edit
            </div>
            <h1 className="title">{formConfig[type].title}</h1>
            <div className="item">
              <img src="" alt="" className="itemImg" />
              <div className="details">
                <h1 className="itemTitle">{documentData?.name}</h1>
                {/* Render form fields in edit mode */}
                {editMode ? (
                  <form onSubmit={handleSave}>
                    {formConfig[type].inputs.map((input, index) => (
                      <div key={index} className="detailItem">
                        <span className="itemKey">{input.label}:</span>
                        <input
                          onChange={handleChange}
                          type={input.type}
                          name={input.id}
                          value={
                            input.type === "checkbox"
                              ? undefined
                              : editedData
                              ? editedData[input.id]
                              : ""
                          }
                          checked={
                            input.type === "checkbox"
                              ? editedData[input.id]
                              : undefined
                          }
                        />
                      </div>
                    ))}
                    {editedData && editedData.volume ? (
                      <>
                        {ingredientVolumeInputs.map((input, index) => (
                          <div key={index} className="detailItem">
                            <span className="itemKey">{input.label}:</span>
                            <input
                              onChange={handleChange}
                              type={input.type}
                              name={input.id}
                              value={
                                input.type === "checkbox"
                                  ? undefined
                                  : editedData
                                  ? editedData[input.id]
                                  : ""
                              }
                              checked={
                                input.type === "checkbox"
                                  ? editedData[input.id]
                                  : undefined
                              }
                            />
                          </div>
                        ))}
                      </>
                    ) : null}

                    {editedData && editedData.minMix && (
                      <div className="detailItem">
                        <span className="itemKey">Min Mix</span>
                        <input
                          id="minMix"
                          onChange={handleMixInput}
                          type="Number"
                          name="minMix"
                          value={editedData["minMix"] / minMixUnit.amount}
                        />
                        <select
                          id="minMix"
                          onChange={(e) => {
                            handleMixUnitChange(e);
                          }}
                        >
                          {MASS_UNITS.map((unit, index) => (
                            <option key={index} value={unit.abbreviation}>
                              {unit.abbreviation}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {editedData && editedData.maxMix && (
                      <div className="detailItem">
                        <span className="itemKey">Max Mix</span>
                        <input
                          id="maxMix"
                          onChange={handleMixInput}
                          type="Number"
                          name="maxMix"
                          value={editedData["maxMix"] / maxMixUnit.amount}
                        />
                        <select
                          id="maxMix"
                          onChange={(e) => {
                            handleMixUnitChange(e);
                          }}
                        >
                          {MASS_UNITS.map((unit, index) => (
                            <option key={index} value={unit.abbreviation}>
                              {unit.abbreviation}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button type="submit">Save</button>
                    <button type="button" onClick={() => setEditMode(false)}>
                      Cancel Edit
                    </button>
                  </form>
                ) : (
                  <>
                    {documentData &&
                      formConfig[type].inputs.map((input, index) => (
                        <div key={index} className="detailItem">
                          <span className="itemKey">{input.label}:</span>
                          {/* Here, let's ensure that documentData[input.name] exists before accessing it */}
                          <span className="itemValue">
                            {documentData[input.id]
                              ? documentData[input.id].toString()
                              : "Empty"}
                          </span>
                        </div>
                      ))}

                    {documentData && documentData.minMix && (
                      <div className="detailItem">
                        <span className="itemKey">Min Mix</span>
                        {(
                          documentData["minMix"] / minMixUnit.amount
                        ).toString()}
                        <select
                          id="minMix"
                          onChange={(e) => {
                            // console.log("unit select");
                            handleMixUnitChange(e);
                          }}
                        >
                          {MASS_UNITS.map((unit, index) => (
                            <option key={index} value={unit.abbreviation}>
                              {unit.abbreviation}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {documentData && documentData.maxMix && (
                      <div className="detailItem">
                        <span className="itemKey">Max Mix</span>
                        {(
                          documentData["maxMix"] / maxMixUnit.amount
                        ).toString()}
                        <select
                          id="maxMix"
                          onChange={(e) => {
                            handleMixUnitChange(e);
                          }}
                        >
                          {MASS_UNITS.map((unit, index) => (
                            <option key={index} value={unit.abbreviation}>
                              {unit.abbreviation}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div></div>

                    {documentData && documentData.volume ? (
                      <>
                        {ingredientVolumeInputs.map((input, index) => (
                          <div key={"vol" + index} className="detailItem">
                            <span className="itemKey">{input.label}:</span>
                            <span className="itemValue">
                              {documentData[input.id]}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="right">
            <div className="left">
              {/* <Chart aspect={3 / 1} title="User Spending ( Last 6 Months)" /> */}
            </div>
            {/* <Chart aspect={3 / 1} title="User Spending ( Last 6 Months)" /> */}
          </div>
        </div>

        {/* {console.log("type ", type)} */}
        <div className="bottom">
          {editMode && (type === "products" || type === "doughs") ? (
            <>
              {editedData.ingredients.length > 0 ? (
                <>
                  {" "}
                  <h1 className="title">Recipe</h1>
                  <h2 className="title">Doughs</h2>
                  <ul>
                    {editedData.ingredients
                      .map((ingredientMap, originalIndex) => ({
                        ingredientMap,
                        originalIndex, // Preserve the original index
                      }))
                      .filter(({ ingredientMap }) =>
                        Object.keys(ingredientMap).some((key) =>
                          key.startsWith("doughs")
                        )
                      )
                      .map(({ ingredientMap, originalIndex }) => (
                        <li key={originalIndex}>
                          {Object.entries(ingredientMap).map(
                            ([ingredientRef, amount], innerIndex) => (
                              <Fragment key={innerIndex}>
                                <select
                                  key={innerIndex}
                                  value={ingredientRef} // Set the select's value to the current ingredient reference
                                  onChange={(e) =>
                                    handleIngredientSelectChange(
                                      e,
                                      originalIndex
                                    )
                                  }
                                >
                                  {ingredientRef.startsWith(
                                    "doughs/"
                                  ) ? null : (
                                    <option key={"Select"} value="">
                                      Select Dough
                                    </option>
                                  )}
                                  {[...sortedDoughs].map((ingredient, idx) => (
                                    <option key={idx} value={ingredient.id}>
                                      {ingredient.name}{" "}
                                      {ingredient.manufacturerCode ? (
                                        <> ({ingredient.manufacturerCode}) </>
                                      ) : null}
                                      {/* ({ingredient.collection}) */}
                                      {
                                        //originalIndex
                                      }
                                    </option>
                                  ))}
                                </select>
                                {/* {console.log("selectedUnits", selectedUnits, " conversionRates ", conversionRates)} */}
                                {ingredientRef &&
                                allItems.find(
                                  (item) => item.id === ingredientRef
                                ) ? (
                                  <>
                                    <input
                                      type="number"
                                      minimum={0.0001}
                                      value={
                                        selectedUnits[originalIndex].isVolume
                                          ? amount /
                                            selectedUnits[originalIndex].unit
                                              .amount /
                                            conversionRates[originalIndex]
                                          : amount /
                                            selectedUnits[originalIndex].unit
                                              .amount
                                      }
                                      onChange={(e) =>
                                        handleIngredientChange(
                                          originalIndex,
                                          ingredientRef,
                                          e.target.value
                                        )
                                      }
                                    />
                                    {allItems.find(
                                      (item) => item.id === ingredientRef
                                    ).volume ? (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            // console.log("unit select");
                                            handleUnitChange(e, originalIndex);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                          {VOLUME_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            // console.log("unit select");
                                            handleUnitChange(e, originalIndex);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    )}
                                  </>
                                ) : null}
                              </Fragment>
                            )
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveItem(originalIndex, "ingredients")
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleAddIngredient("doughs")}
                  >
                    Add Dough
                  </button>{" "}
                  <h2 className="title">Ingredients</h2>
                  <ul>
                    {editedData.ingredients
                      .map((ingredientMap, originalIndex) => ({
                        ingredientMap,
                        originalIndex, // Preserve the original index
                      }))
                      .filter(({ ingredientMap }) =>
                        Object.keys(ingredientMap).some((key) =>
                          key.startsWith("ingredients")
                        )
                      )
                      .map(({ ingredientMap, originalIndex }) => (
                        <li key={originalIndex}>
                          {/* {console.log("ingredientMap ", ingredientMap, originalIndex)} */}
                          {Object.entries(ingredientMap).map(
                            ([ingredientRef, amount], innerIndex) => (
                              <Fragment key={innerIndex}>
                                <select
                                  key={innerIndex}
                                  value={ingredientRef} // Set the select's value to the current ingredient reference
                                  onChange={(e) =>
                                    handleIngredientSelectChange(
                                      e,
                                      originalIndex
                                    )
                                  }
                                >
                                  {ingredientRef.startsWith(
                                    "ingredients/"
                                  ) ? null : (
                                    <option key={"Select"} value="">
                                      Select Ingredient
                                    </option>
                                  )}
                                  {sortedIngredients.map((ingredient, idx) => (
                                    <option key={idx} value={ingredient.id}>
                                      {ingredient.name}{" "}
                                      {ingredient.manufacturerCode ? (
                                        <> ({ingredient.manufacturerCode}) </>
                                      ) : null}
                                      {/* ({ingredient.collection}) {originalIndex} */}
                                    </option>
                                  ))}
                                </select>
                                {ingredientRef &&
                                allItems.find(
                                  (item) => item.id === ingredientRef
                                ) ? (
                                  <>
                                    <input
                                      type="number"
                                      minimum={0.0001}
                                      value={
                                        selectedUnits[originalIndex].isVolume
                                          ? amount /
                                            selectedUnits[originalIndex].unit
                                              .amount /
                                            conversionRates[originalIndex]
                                          : amount /
                                            selectedUnits[originalIndex].unit
                                              .amount
                                      }
                                      onChange={(e) =>
                                        handleIngredientChange(
                                          originalIndex,
                                          ingredientRef,
                                          e.target.value
                                        )
                                      }
                                    />
                                    {allItems.find(
                                      (item) => item.id === ingredientRef
                                    ).volume ? (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            // console.log("unit select");
                                            handleUnitChange(e, originalIndex);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                          {VOLUME_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            // console.log("unit select");
                                            handleUnitChange(e, originalIndex);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, idx) => (
                                            <option
                                              key={idx}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    )}{" "}
                                  </>
                                ) : null}
                              </Fragment>
                            )
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveItem(originalIndex, "ingredients")
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                  </ul>
                </>
              ) : null}

              <button
                type="button"
                onClick={() => handleAddIngredient("ingredients")}
              >
                Add Ingredient
              </button>
            </>
          ) : (
            <>
              {documentData &&
              documentData.ingredients &&
              documentData.ingredients.length > 0 ? (
                <>
                  {" "}
                  <h1 className="title">Recipe</h1>
                  <table>
                    <thead>
                      <tr>
                        <th>Ingredient </th>
                        <th>Quantity </th>
                        {type === "doughs" ? <th>Hydration </th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {documentData.ingredients.map((ingredientMap, index) => (
                        <tr key={index}>
                          {Object.entries(ingredientMap).map(
                            ([ingredientRef, amount], innerIndex) => (
                              <Fragment key={innerIndex}>
                                <td key={innerIndex}>
                                  {getItemNameById(ingredientRef)}
                                </td>
                                <td>
                                  {amount / MASS_UNITS[0].amount}{" "}
                                  {MASS_UNITS[0].abbreviation}
                                </td>
                                {type === "doughs" ? (
                                  <td>
                                    {getItemHydrationById(ingredientRef)} %
                                  </td>
                                ) : null}
                              </Fragment>
                            )
                          )}
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <b>Total</b>
                        </td>
                        <td>
                          {(
                            getTotalValue(documentData.ingredients) /
                            MASS_UNITS[0].amount
                          ).toFixed(2)}{" "}
                          {MASS_UNITS[0].abbreviation}
                        </td>
                        {type === "doughs" ? (
                          <td>
                            {getTotalHydration(
                              documentData.ingredients
                            ).toFixed(2)}{" "}
                            %
                          </td>
                        ) : null}
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : null}
            </>
          )}

          {type === "ingredients" ? (
            editMode && editedData.ingredientUnits ? (
              <>
                {editedData.ingredientUnits.length > 0 ? (
                  <>
                    <h1 className="title">Ingredient Units</h1>
                    <table>
                      <thead>
                        <tr>
                          <th>Unit Name </th>
                          <th>Count </th>
                          <th>Unit </th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedData.ingredientUnits.map((ingredientUnitsMap, index) => (
                          <tr key={index}>
                            {Object.entries(ingredientUnitsMap).map(
                              ([name, number], innerIndex) => (
                                <React.Fragment key={innerIndex}>
                                  <td>
                                    <input
                                      type="string"
                                      value={`${name}`}
                                      onChange={(e) =>
                                        handleIngredientUnitChange(index, e.target.value, "name")
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      min={0.5}
                                      value={
                                        selectedIngredientUnits[index]
                                          ? VOLUME_UNITS.find(
                                              (unit) => unit.abbreviation === selectedIngredientUnits[index].abbreviation
                                            )
                                            ? `${number / editedData.massconversion / selectedIngredientUnits[index].amount}`
                                            : `${number / selectedIngredientUnits[index].amount}`
                                          : `${number / MASS_UNITS[0].amount}`
                                      }
                                      onChange={(e) => handleIngredientUnitChange(index, e.target.value, "count")}
                                    />
                                  </td>
                                  <td>
                                    {editedData.volume ? (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            handleIngredientWeightUnitChange(e, index);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, index) => (
                                            <option
                                              key={index}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                          {VOLUME_UNITS.map((unit, index) => (
                                            <option
                                              key={index}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          onChange={(e) => {
                                            handleIngredientWeightUnitChange(e, index);
                                          }}
                                        >
                                          {MASS_UNITS.map((unit, index) => (
                                            <option
                                              key={index}
                                              value={unit.abbreviation}
                                            >
                                              {unit.abbreviation}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    )}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveItem(index, "ingredientUnits")}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </React.Fragment>
                              )
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : null}

                <button type="button" onClick={handleAddIngredientUnit}>
                  Add Unit
                </button>
              </>
            ) : (
              <>
                {documentData?.ingredientUnits?.length > 0 ? (
                  <>
                    <h1 className="title">Ingredient Units </h1>
                    <table>
                      <thead>
                        <tr>
                          <th>Unit </th>
                          <th>Count </th>
                          <th>Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentData.ingredientUnits.map((ingredientUnitsMap, index) => (
                          <tr key={index}>
                            {Object.entries(ingredientUnitsMap).map(
                              ([name, number], innerIndex) => (
                                <React.Fragment key={innerIndex}>
                                  <td>{`${name}`}</td>
                                  <td>{`${number / MASS_UNITS[0].amount}`}</td>
                                  <td>{MASS_UNITS[0].abbreviation}</td>
                                </React.Fragment>
                              )
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : null}
              </>
            )
          ) : null}

          {type === "products" ? (
            editMode && editedData.productUnits ? (
              <>
                <h1 className="title">Product Units</h1>
                <ul>
                  {editedData.productUnits.map((productUnitMap, index) => (
                    <li key={index}>
                      <Fragment key={index}>
                        {productUnitMap !== "" ? (
                          <>
                            <input
                              type="string"
                              value={productUnitMap.name}
                              onChange={(e) =>
                                handleProductUnitChange(index, e.target.value, "name")
                              }
                            />
                            <input
                              type="number"
                              minimum={0.5}
                              value={productUnitMap.pieceCount}
                              onChange={(e) =>
                                handleProductUnitChange(index, e.target.value, "count")
                              }
                            />
                          </>
                        ) : null}
                      </Fragment>

                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index, "productUnits")}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                <button type="button" onClick={handleAddProductUnit}>
                  Add Unit
                </button>
              </>
            ) : (
              <>
                {documentData && documentData.productUnits ? (
                  <>
                    <h1 className="title">Product Units </h1>
                    <table>
                      <thead>
                        <tr>
                          <th>Unit </th>
                          <th>Count </th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentData.productUnits.map(
                          (productUnitstMap, index) => (
                            <tr key={index}>
                              <Fragment key={index}>
                                <td key={index}>{productUnitstMap.name}</td>
                                <td>{productUnitstMap.pieceCount}</td>
                              </Fragment>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </>
                ) : null}
              </>
            )
          ) : null}

          {type === "lines" ? (
            <>
              {editMode && editedData.products ? (
                <>
                  <h1 className="title">Products</h1>
                  {editedData.products.length > 0 ? (
                    <>
                      <table>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>piecesPerHour</th>
                            <th>Scrap Factor %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editedData.products.map((productMap, index) => (
                            <tr key={index}>
                              {Object.entries(productMap).map(
                                (
                                  [productRef, { piecesPerHour, scrapPercent }],
                                  innerIndex
                                ) => {
                                  // Get all selected product IDs so far
                                  const selectedProductIds = editedData.products
                                    .map((p) => Object.keys(p)[0])
                                    .filter((id) => id !== productRef);
                                  // Exclude already-selected products, but allow current one
                                  const availableProducts =
                                    sortedProducts.filter(
                                      (product) =>
                                        !selectedProductIds.includes(product.id)
                                    );

                                  return (
                                    <Fragment key={innerIndex}>
                                      <td>
                                        <select
                                          key={innerIndex}
                                          value={productRef}
                                          onChange={(e) =>
                                            handleProductSelectChange(e, index)
                                          }
                                        >
                                          <option key={"Select"} value="">
                                            Select a Product
                                          </option>
                                          {availableProducts.map(
                                            (product, idx) => (
                                              <option
                                                key={idx}
                                                value={product.id}
                                              >
                                                {product.name} (
                                                {product.collection})
                                              </option>
                                            )
                                          )}
                                        </select>
                                      </td>
                                      {productRef !== "" ? (
                                        <>
                                          <td>
                                            <input
                                              type="number"
                                              minimum={0.0001}
                                              name="piecesPerHour"
                                              value={piecesPerHour}
                                              onChange={(e) =>
                                                handleProductChange(
                                                  index,
                                                  productRef,
                                                  e
                                                )
                                              }
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              name="scrapPercent"
                                              minimum={0.0001}
                                              value={scrapPercent}
                                              onChange={(e) =>
                                                handleProductChange(
                                                  index,
                                                  productRef,
                                                  e
                                                )
                                              }
                                            />
                                          </td>
                                        </>
                                      ) : null}
                                    </Fragment>
                                  );
                                }
                              )}
                              <td>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveItem(index, "products")
                                  }
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : null}
                  <button type="button" onClick={handleAddProduct}>
                    Add Product
                  </button>
                </>
              ) : (
                <>
                  {documentData &&
                  documentData.products &&
                  documentData.products.length > 0 ? (
                    <>
                      <h1 className="title">Products</h1>
                      <table>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Pieces/Hour</th>
                            <th>Scrap %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentData.products
                            .sort((a, b) => {
                              const aRef = Object.keys(a)[0];
                              const bRef = Object.keys(b)[0];
                              return getItemNameById(aRef).localeCompare(
                                getItemNameById(bRef)
                              );
                            })
                            .map((productMap, index) => {
                              const [
                                productRef,
                                { piecesPerHour, scrapPercent },
                              ] = Object.entries(productMap)[0];

                              return (
                                <tr key={index}>
                                  <td>{getItemNameById(productRef)}:</td>
                                  <td>{piecesPerHour}</td>
                                  <td>{scrapPercent}%</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </>
                  ) : null}
                </>
              )}
            </>
          ) : null}

          {type === "ovens" ? (
            <>
              {editMode && editedData.products ? (
                <>
                  <h1 className="title">Oven Product Throughput</h1>
                  {editedData.products.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Pieces/Hour</th>
                          <th>Capacity/Batch</th>
                          <th>Bake Time (min)</th>
                          <th>Preheat (min)</th>
                          <th>Cooldown (min)</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedData.products.map((productMap, index) => {
                          const [productRef, config = {}] = Object.entries(productMap || {})[0] || ["", {}];
                          const selectedProductIds = editedData.products
                            .map((p) => Object.keys(p || {})[0])
                            .filter((id) => id && id !== productRef);
                          const availableProducts = sortedProducts.filter(
                            (product) => !selectedProductIds.includes(product.id)
                          );

                          return (
                            <tr key={index}>
                              <td>
                                <select
                                  value={productRef}
                                  onChange={(e) => handleProductSelectChange(e, index)}
                                >
                                  <option value="">Select a Product</option>
                                  {availableProducts.map((product, idx) => (
                                    <option key={idx} value={product.id}>
                                      {product.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {productRef ? (
                                <>
                                  <td>
                                    <input
                                      type="number"
                                      min={0}
                                      name="piecesPerHour"
                                      value={config.piecesPerHour || ""}
                                      onChange={(e) => handleProductChange(index, productRef, e)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      min={0}
                                      name="capacity"
                                      value={config.capacity || ""}
                                      onChange={(e) => handleProductChange(index, productRef, e)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      min={0}
                                      name="bakeTimeMinutes"
                                      value={config.bakeTimeMinutes || ""}
                                      onChange={(e) => handleProductChange(index, productRef, e)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      min={0}
                                      name="preheatMinutes"
                                      value={config.preheatMinutes || ""}
                                      onChange={(e) => handleProductChange(index, productRef, e)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      min={0}
                                      name="cooldownMinutes"
                                      value={config.cooldownMinutes || ""}
                                      onChange={(e) => handleProductChange(index, productRef, e)}
                                    />
                                  </td>
                                </>
                              ) : (
                                <td colSpan={5}></td>
                              )}
                              <td>
                                <button type="button" onClick={() => handleRemoveItem(index, "products")}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : null}
                  <button type="button" onClick={handleAddProduct}>
                    Add Product
                  </button>
                </>
              ) : (
                <>
                  <h1 className="title">Oven Product Throughput</h1>
                  {normalizeOvenProducts(documentData?.products).length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Pieces/Hour</th>
                          <th>Capacity/Batch</th>
                          <th>Bake Time (min)</th>
                          <th>Preheat (min)</th>
                          <th>Cooldown (min)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalizeOvenProducts(documentData?.products).map((productMap, index) => {
                          const [productRef, config = {}] = Object.entries(productMap || {})[0] || ["", {}];
                          return (
                            <tr key={index}>
                              <td>{getItemNameById(productRef)}</td>
                              <td>{config.piecesPerHour || 0}</td>
                              <td>{config.capacity || documentData?.capacity || 0}</td>
                              <td>{config.bakeTimeMinutes || documentData?.bakeTimeMinutes || 0}</td>
                              <td>{config.preheatMinutes || documentData?.preheatMinutes || 0}</td>
                              <td>{config.cooldownMinutes || documentData?.cooldownMinutes || 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p>No product rates configured for this oven.</p>
                  )}
                </>
              )}
            </>
          ) : null}

          {/* --- PRODUCTIONLISTS: flattened-aware view/edit --- */}
          {type === "productionlists" ? (
            <>
              {editMode ? (
                <>
                  <h1 className="title">Production Order (editable)</h1>
                  <ProductionListsForm
                    flattenedLinesAndProducts={flattenedLinesAndProducts}
                    onAddBlankPair={addBlankPair}
                    onUpdatePair={updatePair}
                    onRemovePair={removePair}
                    onMovePair={movePair}
                    onAddAllProductsForLine={addAllProductsForLine}
                    availableProductsForLine={availableProductsForLine}
                    isLineSelectable={isLineSelectable}
                    sortedLines={sortedLines}
                    productionLines={sortedLines}
                    getItemNameByProductRef={(ref) => getItemNameById(ref)}
                  />
                </>
              ) : (
                <>
                  <h1 className="title">Production Order</h1>
                  {documentData?.linesAndProducts &&
                  documentData.linesAndProducts.length > 0 ? (
                    <>
                      {(() => {
                        const canonical = isFlattenedFormat(documentData.linesAndProducts)
                          ? documentData.linesAndProducts
                          : normalizeToFlattened(documentData.linesAndProducts);
                        return (
                          <ol>
                            {canonical.map((pair, idx) => {
                              const lineRef = pair.lineId || pair.line || "";
                              const productRef = pair.productId || pair.product || "";
                              const lineName = (findLineByRef(lineRef) || getLineById(lineRef)).name || "Unknown";
                              const productName = getItemNameById(productRef);
                              return (
                                <li key={`${lineRef}-${productRef}-${idx}`}>
                                  <strong>{idx + 1}.</strong>{" "}
                                  Line: <em>{lineName}</em> — Product: <em>{productName}</em>
                                </li>
                              );
                            })}
                          </ol>
                        );
                      })()}
                    </>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
export default Single;
